/*  NarrationPlayer — iter100.157n
    ────────────────────────────────────────────────────────────
    Doctor-side audio player that streams the lesson's narration
    MP3 (fetched with the auth token, then rendered as a blob URL).

    Reports:
      • `audio_progress` (0..1) to the parent whenever the listener
        crosses a new decile (10%, 20%, …, 100%). Used by CourseView
        to unlock the "mark viewed" button once ≥90% is reached.

    Persistence:
      • Playback speed is remembered per lesson in localStorage.
      • Resume position is remembered per lesson in localStorage.
*/
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { SpeakerHigh, LockKey } from "@phosphor-icons/react";
import { API } from "@/lib/api";


const SPEED_OPTIONS = [0.75, 1.0, 1.25, 1.5, 2.0];
const PROGRESS_KEY = (lessonId) => `invq_narration_pos_${lessonId}`;
const SPEED_KEY = (lessonId) => `invq_narration_speed_${lessonId}`;


export default function NarrationPlayer({ lesson, onProgress, gateStrict }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [speed, setSpeed] = useState(() => {
        const stored = localStorage.getItem(SPEED_KEY(lesson.id));
        return stored ? Number(stored) : 1.0;
    });
    const [progressPct, setProgressPct] = useState(0);
    const audioRef = useRef(null);
    const lastReportedDecileRef = useRef(0);

    // Fetch the audio blob
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        setBlobUrl(null);
        (async () => {
            try {
                const token = localStorage.getItem("ca_token");
                const resp = await fetch(
                    `${API}/academy/lessons/${lesson.id}/narration/audio`,
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                if (!resp.ok) throw new Error(await resp.text());
                const blob = await resp.blob();
                if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
            } catch (e) {
                if (!cancelled) {
                    setError(String(e));
                    toast.error("Couldn't load narration audio.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [lesson.id]);

    // Apply persisted speed + resume position
    useEffect(() => {
        const el = audioRef.current;
        if (!el || !blobUrl) return;
        el.playbackRate = speed;
        const resumeAt = Number(localStorage.getItem(PROGRESS_KEY(lesson.id)) || 0);
        if (resumeAt > 0 && resumeAt < 1) {
            const applyResume = () => {
                if (el.duration && !isNaN(el.duration)) {
                    el.currentTime = resumeAt * el.duration;
                }
            };
            if (el.readyState >= 1) applyResume();
            else el.addEventListener("loadedmetadata", applyResume, { once: true });
        }
    }, [blobUrl, lesson.id, speed]);

    const handleTimeUpdate = useCallback(() => {
        const el = audioRef.current;
        if (!el || !el.duration || isNaN(el.duration)) return;
        const pct = el.currentTime / el.duration;
        setProgressPct(pct);
        localStorage.setItem(PROGRESS_KEY(lesson.id), String(pct));
        const decile = Math.floor(pct * 10);
        if (decile > lastReportedDecileRef.current) {
            lastReportedDecileRef.current = decile;
            onProgress?.(pct);
        }
    }, [lesson.id, onProgress]);

    const handleEnded = useCallback(() => {
        setProgressPct(1);
        localStorage.setItem(PROGRESS_KEY(lesson.id), "1");
        onProgress?.(1);
    }, [lesson.id, onProgress]);

    const changeSpeed = (next) => {
        setSpeed(next);
        localStorage.setItem(SPEED_KEY(lesson.id), String(next));
        if (audioRef.current) audioRef.current.playbackRate = next;
    };

    const pctLabel = `${Math.round(progressPct * 100)}%`;
    const gateMet = progressPct >= 0.9;

    return (
        <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-4 space-y-3" data-testid={`narration-player-${lesson.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-sky-100 text-sky-700">
                    <SpeakerHigh size={17} weight="fill" />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">Listen to this lesson</div>
                    <div className="text-[11px] text-muted-foreground">
                        {gateStrict ? (
                            <span className="inline-flex items-center gap-1">
                                <LockKey size={11} weight="duotone" /> Listen to at least 90% to progress · {pctLabel} complete
                            </span>
                        ) : (
                            <>Progress: {pctLabel}</>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-xs text-muted-foreground italic">Loading audio…</div>
            ) : error ? (
                <div className="text-xs text-rose-600">Couldn&apos;t load audio. Refresh the page and try again.</div>
            ) : (
                <>
                    <audio
                        ref={audioRef}
                        controls
                        controlsList="nodownload"
                        src={blobUrl || undefined}
                        onTimeUpdate={handleTimeUpdate}
                        onEnded={handleEnded}
                        className="w-full"
                        data-testid={`narration-audio-${lesson.id}`}
                    />
                    <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-[11px] font-semibold text-muted-foreground mr-1">Speed:</span>
                        {SPEED_OPTIONS.map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => changeSpeed(s)}
                                className={`px-2 py-0.5 rounded-md border text-[11px] font-bold transition-colors ${
                                    Math.abs(speed - s) < 0.01
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-white border-border hover:bg-secondary"
                                }`}
                                data-testid={`narration-speed-${s}-${lesson.id}`}
                            >
                                {s}×
                            </button>
                        ))}
                        {gateStrict && (
                            <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${
                                gateMet
                                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                    : "bg-amber-50 text-amber-800 border-amber-200"
                            }`}>
                                {gateMet ? "Ready to continue" : `${Math.max(0, Math.ceil((0.9 - progressPct) * 100))}% left to unlock`}
                            </span>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
